import React, { useEffect, useState } from 'react';
import ProcessSelector from './ProcessSelector';
import DraggableSteps from './DraggableSteps';
import Tooltip from './Tooltip';
import teamService from '../../services/teamService';
import matrixData from '../../data/matrix.json';
import { filterMatrixRow, findMatrixRow, extractTeamNumber, MATRIX_COLUMNS } from '../../shared/matrix';
import './MatricParser.css';

const MatricParser = ({ onDataLoad, theme, selectedTeam, onStepsChange, onNavigateToDashboard }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    product: '',
    action: ''
  });
  const [steps, setSteps] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [fireworksActive, setFireworksActive] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [duplicateStepCode, setDuplicateStepCode] = useState('');

  useEffect(() => {
    // СТАРЫЙ КОД - ЗАКОММЕНТИРОВАН (можно вернуть если нужно)
    /*
    fetch('/matrica.csv')
      .then(response => {
        if (!response.ok) {
          throw new Error('Ошибка загрузки файла');
        }
        return response.text();
      })
      .then(csvText => {
        Papa.parse(csvText, {
          header: false,
          delimiter: ';',
          complete: (results) => {
            setData(results.data);
            setLoading(false);
            // Передаем данные в родительский компонент
            if (onDataLoad) {
              onDataLoad(results.data);
            }
          },
          error: (error) => {
            setError('Ошибка парсинга CSV файла');
            setLoading(false);
          }
        });
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
    */

    const localData = matrixData;

        setData(localData);
    setLoading(false);
    
    // Передаем данные в родительский компонент
    if (onDataLoad) {
      onDataLoad(localData);
    }
  }, []); // УБРАЛ onDataLoad из зависимостей - это исправляет бесконечный цикл

  // Загружаем шаги команды при изменении выбранной команды
  useEffect(() => {
    const loadTeamSteps = async () => {
      if (!selectedTeam) {
        return;
      }

      try {
        const teamData = await teamService.getTeam(selectedTeam);
        setSteps(teamData?.steps || []);
      } catch (loadError) {
        console.error('Ошибка загрузки шагов команды:', loadError);
        setSteps([]);
        setError('Не удалось загрузить шаги команды с сервера');
      }
    };

    loadTeamSteps();
  }, [selectedTeam]);

  const filteredData = data.filter(row => filterMatrixRow(row, filters) && row[MATRIX_COLUMNS.TEAM_NUMBER] === extractTeamNumber(selectedTeam));

  // Всегда можно добавить шаг
  const canAddStep = filters.category && filters.product && filters.action;

  const handleAddStep = () => {
    if (canAddStep) {
      // Find the matching row to get the code
      const matchingRow = findMatrixRow(data, {
        category: filters.category,
        product: filters.product,
        action: filters.action,
        teamName: selectedTeam,
      });

      const code = matchingRow ? matchingRow[MATRIX_COLUMNS.CODE] : '';
      
      // СТАРЫЙ КОД - ЗАКОММЕНТИРОВАН (можно вернуть если нужно)
      /*
      const newSteps = [
        ...steps,
        {
          category: filters.category,
          product: filters.product,
          action: filters.action,
          code: code
        }
      ];
      
      setSteps(newSteps);
      setFilters({ category: '', product: '', action: '' });
      
      // Сохраняем шаги для команды
      if (selectedTeam && onStepsChange) {
        onStepsChange(newSteps);
      }
      */
      
      // НОВЫЙ КОД - проверка на дублирование шагов (ЗАКОММЕНТИРОВАТЬ ПРИ ОТКАТЕ)
      // Проверяем, есть ли уже шаг с таким кодом в процессе
      const isStepAlreadyExists = steps.some(step => step.code === code);
      
      // Проверяем, есть ли уже шаг с такой же комбинацией "Кто + Действие"
      const isCategoryProductExists = steps.some(step => 
        step.category === filters.category && 
        step.product === filters.product
      );
      
      if (isStepAlreadyExists) {
        // Показываем красивую всплывашку о дублировании
        setDuplicateStepCode(code);
        setShowDuplicateAlert(true);
        
        // Автоматически скрываем всплывашку через 4 секунды
        setTimeout(() => {
          setShowDuplicateAlert(false);
        }, 4000);
        
        return; // Прерываем добавление дублирующегося шага
      }
      
      if (isCategoryProductExists) {
        // Показываем всплывашку о дублировании комбинации "Кто + Действие"
        const stepDescription = `${filters.category} → ${filters.product}`;
        setDuplicateStepCode(stepDescription);
        setShowDuplicateAlert(true);
        
        // Автоматически скрываем всплывашку через 4 секунды
        setTimeout(() => {
          setShowDuplicateAlert(false);
        }, 4000);
        
        return; // Прерываем добавление дублирующегося шага
      }
      
      // Если шаг не дублируется - добавляем его в процесс
      const newSteps = [
        ...steps,
        {
          category: filters.category,
          product: filters.product,
          action: filters.action,
          code: code
        }
      ];
      
      setSteps(newSteps);
      setFilters({ category: '', product: '', action: '' });
      
      // Сохраняем шаги для команды
      if (selectedTeam && onStepsChange) {
        onStepsChange(newSteps);
      }
      // КОНЕЦ НОВОГО КОДА - проверка на дублирование шагов
    }
  };

  const handleClearSteps = () => {
    setSteps([]);
    
    // Сохраняем пустой массив шагов для команды
    if (selectedTeam && onStepsChange) {
      onStepsChange([]);
    }
  };

  const handleRemoveStep = (indexToRemove) => {
    const newSteps = steps.filter((_, index) => index !== indexToRemove);
    setSteps(newSteps);
    
    // Сохраняем шаги для команды
    if (selectedTeam && onStepsChange) {
      onStepsChange(newSteps);
    }
  };

  // Функции для модального окна подтверждения
  const handleSendResult = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSend = () => {
    // Проверка минимального количества шагов
    if (steps.length < 4) {
      alert('Необходимо добавить минимум 4 шага перед отправкой результата');
      setShowConfirmModal(false);
      return;
    }
    
    // Здесь будет логика отправки результата
    console.log('Отправка результата:', steps);
    
    // Сохраняем шаги для команды перед отправкой
    if (selectedTeam && onStepsChange) {
      onStepsChange(steps);
    }
    
    // Закрываем модалку подтверждения и запускаем анимацию
    setShowConfirmModal(false);
    setFireworksActive(true);
    
    // Показываем модалку успеха через небольшую задержку
    setTimeout(() => {
      setShowSuccessModal(true);
    }, 500);
  };

  const handleCancelSend = () => {
    setShowConfirmModal(false);
  };

  const handleGoToDashboard = () => {
    setShowSuccessModal(false);
    setFireworksActive(false);
    
    // Небольшая задержка для завершения анимации перед переходом
    setTimeout(() => {
      // Переходим в дашборд через callback с принудительным обновлением
      if (onNavigateToDashboard) {
        onNavigateToDashboard();
      }
    }, 300);
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    setFireworksActive(false);
  };

  // Drag&Drop всегда доступен
  const handleDragEnd = (result) => {
    if (!result || !steps || steps.length === 0) {
      return;
    }

    if (!result.destination) {
      return; // Если элемент не был перемещен
    }

    const sourceIndex = result.source.index;
    const destinationIndex = result.destination.index;

    if (sourceIndex === destinationIndex) {
      return; // Если элемент остался на том же месте
    }

    // Проверяем валидность индексов
    if (sourceIndex < 0 || sourceIndex >= steps.length || 
        destinationIndex < 0 || destinationIndex >= steps.length) {
      return;
    }

    // Создаем новый массив шагов
    const newSteps = Array.from(steps);
    const [removed] = newSteps.splice(sourceIndex, 1);
    newSteps.splice(destinationIndex, 0, removed);

    setSteps(newSteps);
    
    // Сохраняем шаги для команды
    if (selectedTeam && onStepsChange) {
      onStepsChange(newSteps);
    }
  };

  // Удаляем обработчик блокировки
  // const handleLockChange = (locked) => { setIsTeamLocked(locked); };

  if (loading) {
    return (
      <div className={`parser-container ${theme === 'dark' ? 'dark-theme' : ''}`}>
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Загрузка данных...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`parser-container ${theme === 'dark' ? 'dark-theme' : ''}`}>
        <div className="error-state">
          <p>❌ {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`parser-container ${theme === 'dark' ? 'dark-theme' : ''}`}>
      <div className="parser-header">
        <h2>Конструктор процессов</h2>
        <p>Выберите последовательность шагов для создания медицинского процесса</p>
      </div>
      {/* Удаляем TeamLockStatus и lock-warning */}
      <ProcessSelector filters={filters} setFilters={setFilters} data={data} />
      {canAddStep && (
        <div className="add-step-row fade-in">
          <button className="btn btn-primary" onClick={handleAddStep}>
            ➕ Добавить в процесс
          </button>
        </div>
      )}
      {/* Удаляем lock-warning */}
      {steps.length > 0 && (
        <div className="process-steps-list fade-in">
          <div className="steps-header">
            <span>Ваш процесс:</span>
            <button 
              className="btn btn-secondary" 
              onClick={handleClearSteps} 
              style={{marginLeft: 16}}
            >
              Очистить шаги
            </button>
            <div style={{marginLeft: 8, justifySelf:'end', alignItems: 'center', gap: 8}}>
              {steps.length < 4 ? (
                <Tooltip 
                  content="Необходимо добавить минимум 4 шага перед отправкой результата"
                  position="top"
                >
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSendResult}
                    disabled={steps.length < 4}
                  >
                    Отправить результат
                  </button>
                </Tooltip>
              ) : (
                <button 
                  className="btn btn-primary" 
                  onClick={handleSendResult}
                  disabled={steps.length < 4}
                >
                  Отправить результат
                </button>
              )}
            </div>
          </div>
          <DraggableSteps 
            steps={steps}
            onDragEnd={handleDragEnd}
            onRemoveStep={handleRemoveStep}
            isLocked={false} // всегда false
          />
        </div>
      )}
      {showConfirmModal && (
        <div className="modal-overlay" onClick={handleCancelSend}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>📤 Отправить результат</h3>
              <button className="modal-close" onClick={handleCancelSend}>✕</button>
            </div>
            <div className="modal-body">
              <div className="confirmation-icon">❓</div>
              <p className="confirmation-text">Вы уверены, что хотите отправить результат?</p>
              <div className="steps-summary">
                <h4>Сводка процесса:</h4>
                <div className="steps-preview">
                  {steps.map((step, index) => (
                    <div key={index} className="step-preview-item">
                      <div className="step-number">{index + 1}</div>
                      <div className="step-details">
                        <div className="step-detail-row">
                          <span className="step-detail-label">Кто совершил действие:</span>
                          <span className="step-detail-value">{step.category}</span>
                        </div>
                        <div className="step-detail-row">
                          <span className="step-detail-label">Действие:</span>
                          <span className="step-detail-value">{step.product}</span>
                        </div>
                        <div className="step-detail-row">
                          <span className="step-detail-label">Способ:</span>
                          <span className="step-detail-value">{step.action}</span>
                        </div>
                        <div className="step-detail-row">
                          <span className="step-detail-label">Код:</span>
                      <span className="step-code">{step.code}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCancelSend}>
                Отмена
              </button>
              <button className="btn btn-primary fireworks-trigger" onClick={handleConfirmSend}>
                ✅ Отправить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Анимация фейрверка */}
      {fireworksActive && (
        <div className="fireworks-container">
          <div className="firework firework-1"></div>
          <div className="firework firework-2"></div>
          <div className="firework firework-3"></div>
          <div className="firework firework-4"></div>
          <div className="firework firework-5"></div>
          <div className="firework firework-6"></div>
          <div className="firework firework-7"></div>
          <div className="firework firework-8"></div>
        </div>
      )}

      {/* Модалка успеха */}
      {showSuccessModal && (
        <div className="modal-overlay success-overlay" onClick={handleCloseSuccessModal}>
          <div className="modal-content success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="success-header">
              <div className="success-icon">🎉</div>
              <h3>Поздравляем!</h3>
            </div>
            <div className="success-body">
              <p className="success-text">
                Ваш результат успешно отправлен! 🚀
              </p>
              <p className="success-subtext">
                Процесс сохранен и готов к анализу. Хотите посмотреть статистику в дашборде?
              </p>
              <div className="success-stats">
                <div className="stat-item">
                  <span className="stat-number">{steps.length}</span>
                  <span className="stat-label">шагов отправлено</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">100%</span>
                  <span className="stat-label">успешно</span>
                </div>
              </div>
            </div>
            <div className="success-footer">
              <button className="btn btn-secondary" onClick={handleCloseSuccessModal}>
                Остаться здесь
              </button>
              <button className="btn btn-primary dashboard-btn" onClick={handleGoToDashboard}>
                📊 Перейти в дашборд
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Всплывашка дублирования шага */}
      {showDuplicateAlert && (
        <div className="duplicate-alert">
          <div className="alert-content">
            <div className="alert-icon">⚠️</div>
            <div className="alert-text">
              <h4>Шаг уже добавлен</h4>
              <p>Шаг "{duplicateStepCode}" уже присутствует в процессе</p>
            </div>
            <button className="alert-close" onClick={() => setShowDuplicateAlert(false)}>
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatricParser; 